import { create } from "zustand";
import { AI_HUB_DEFAULT_PAGE_SIZE, type AiHubTabKey } from "./constants";
import type { ObjectAnalysisView } from "./ObjectAnalysis/ObjectAnalysisFilterBar";

export type AiHubTaggingListFilters = Readonly<{
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValue: string;
  objectId: string;
}>;

export type AiHubObjectAnalysisListFilters = Readonly<{
  planIds: string[];
  fromDateIso: string | null;
  toDateIso: string | null;
  view: ObjectAnalysisView;
  page: number;
  pageSize: number;
}>;

export type AiHubImplementationRecordListFilters = Readonly<{
  keyword: string;
  assessmentSource: string[];
  assessmentName: string[];
  impactedObjectType: string[];
  page: number;
  pageSize: number;
}>;

export const EMPTY_AI_HUB_TAGGING_FILTERS: AiHubTaggingListFilters = {
  objectType: "",
  objectSubType: "",
  conceptCode: "",
  conceptValue: "",
  objectId: "",
};

export const EMPTY_AI_HUB_OBJECT_ANALYSIS_FILTERS: AiHubObjectAnalysisListFilters =
  {
    planIds: [],
    fromDateIso: null,
    toDateIso: null,
    view: "list",
    page: 0,
    pageSize: AI_HUB_DEFAULT_PAGE_SIZE,
  };

export const EMPTY_AI_HUB_IMPLEMENTATION_RECORD_FILTERS: AiHubImplementationRecordListFilters =
  {
    keyword: "",
    assessmentSource: [],
    assessmentName: [],
    impactedObjectType: [],
    page: 0,
    pageSize: AI_HUB_DEFAULT_PAGE_SIZE,
  };

type AiHubListFiltersState = {
  activeTab: AiHubTabKey;
  taggingResult: AiHubTaggingListFilters;
  conditionalTaggingResult: AiHubTaggingListFilters;
  objectAnalysis: AiHubObjectAnalysisListFilters;
  implementationRecords: AiHubImplementationRecordListFilters;
  setActiveTab: (tab: AiHubTabKey) => void;
  setTaggingResult: (patch: Partial<AiHubTaggingListFilters>) => void;
  setConditionalTaggingResult: (
    patch: Partial<AiHubTaggingListFilters>,
  ) => void;
  setObjectAnalysis: (patch: Partial<AiHubObjectAnalysisListFilters>) => void;
  setImplementationRecords: (
    patch: Partial<AiHubImplementationRecordListFilters>,
  ) => void;
};

export const useAiHubListFiltersStore = create<AiHubListFiltersState>(
  (set) => ({
    activeTab: "taggingResult",
    taggingResult: EMPTY_AI_HUB_TAGGING_FILTERS,
    conditionalTaggingResult: EMPTY_AI_HUB_TAGGING_FILTERS,
    objectAnalysis: EMPTY_AI_HUB_OBJECT_ANALYSIS_FILTERS,
    implementationRecords: EMPTY_AI_HUB_IMPLEMENTATION_RECORD_FILTERS,
    setActiveTab: (tab) => set({ activeTab: tab }),
    setTaggingResult: (patch) =>
      set((state) => ({
        taggingResult: { ...state.taggingResult, ...patch },
      })),
    setConditionalTaggingResult: (patch) =>
      set((state) => ({
        conditionalTaggingResult: {
          ...state.conditionalTaggingResult,
          ...patch,
        },
      })),
    setObjectAnalysis: (patch) =>
      set((state) => ({
        objectAnalysis: { ...state.objectAnalysis, ...patch },
      })),
    setImplementationRecords: (patch) =>
      set((state) => ({
        implementationRecords: { ...state.implementationRecords, ...patch },
      })),
  }),
);

export function resetAiHubListFilters() {
  useAiHubListFiltersStore.setState({
    activeTab: "taggingResult",
    taggingResult: EMPTY_AI_HUB_TAGGING_FILTERS,
    conditionalTaggingResult: EMPTY_AI_HUB_TAGGING_FILTERS,
    objectAnalysis: EMPTY_AI_HUB_OBJECT_ANALYSIS_FILTERS,
    implementationRecords: EMPTY_AI_HUB_IMPLEMENTATION_RECORD_FILTERS,
  });
}
